import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, durationLabel, isoDay, longDay, parseDay } from '../../lib/quickAdd';
import MoreMenu, { swatch } from './MoreMenu';
import { Avatar, Dialog, ItTag } from './TodoPeople';
import { ClientChip } from './TodoClients';
import { LockIcon } from './TodoDeps';
import { Icon } from './icons';
import './TodoWorkload.css';

/*
 * THE TEAM'S WORKLOAD (migration 072).
 *
 * Gordon: who has what, and by when - so work piling up on one person can be
 * seen, and handed to somebody else by dragging it across. A row ("lane") per
 * person, and one for what nobody on a shared list has been given yet; a
 * column per bucket: overdue, today, each day left this week (Mon-Sun), next
 * week, later, no date. Each cell says how many and shows them, and is tinted
 * by how full it is - the "capacity feel". Where to-dos say how long they
 * take (073), cells and lanes add the hours up and a day is tinted by them
 * (loadOf).
 *
 * WHAT IS IN IT is the server's to decide (lib/todo-workload.js): for an
 * administrator everybody; for a member of staff the lists they are on and
 * the people on them, never a to-do on a list they are not on.
 *
 * MOVING A TO-DO. Dragged to another lane, it is given to that person - the
 * same assignment, rules, activity and bell as the picker in the to-do
 * (POST /todos/workload/assign); a lane it may not go to (somebody not on its
 * list, or out of somebody's own list) does not light up. Dragged to another
 * column, it is rescheduled (PATCH due_date) when it is on a list the viewer
 * is on. Both are also in every card's ⋯ - "Give to…" and "Reschedule…" -
 * which is the keyboard's way and the phone's: on a phone there is no grid
 * and no drag, just each person's to-dos stacked, by when.
 */

const DRAG_AFTER = 5;       // px before a press on a card becomes a drag
const SHOW = 4;             // cards shown in a cell before "+N more"

/** A to-do's bucket, against `today` and the week starting `monday` (both ISO days). */
export function bucketOf(item, today, monday) {
  const d = item.due_date ? String(item.due_date).slice(0, 10) : null;
  if (!d) return 'none';
  if (d < today) return 'overdue';
  const sunday = isoDay(addDays(parseDay(monday), 6));
  if (d <= sunday) return d;
  const nextSunday = isoDay(addDays(parseDay(monday), 13));
  return d <= nextSunday ? 'next' : 'later';
}

/** The columns, left to right: [{ key, label, sub, day, target }]. */
export function columnsFor(today, monday) {
  const cols = [{ key: 'overdue', label: 'Overdue', target: null }];
  const start = parseDay(today);
  const sunday = isoDay(addDays(parseDay(monday), 6));
  for (let i = 0; i < 7; i += 1) {
    const d = isoDay(addDays(start, i));
    if (d > sunday) break;
    const [wd, ...rest] = longDay(d).split(' ');
    cols.push({ key: d, label: i === 0 ? 'Today' : wd, sub: i === 0 ? longDay(d) : rest.join(' '), day: d, target: d });
  }
  const nextMonday = isoDay(addDays(parseDay(monday), 7));
  cols.push({ key: 'next', label: 'Next week', sub: `w/c ${longDay(nextMonday).split(' ').slice(1).join(' ')}`, target: nextMonday });
  cols.push({ key: 'later', label: 'Later', target: null });
  cols.push({ key: 'none', label: 'No date', target: 'none' });
  return cols;
}

/*
 * HOURS (073). Where to-dos say how long they take, a cell and a lane add
 * them up ("3 · 5 h"); the counts stay, because a to-do with no estimate is
 * still work. A single day's cell with any estimate in it is tinted by its
 * hours against a working day - up to half a day light, up to a day busy,
 * more than a day heavy; every other cell, by how many.
 */
const minutesOf = (items) => items.reduce((n, t) => n + (Number(t.duration_minutes) > 0 ? Number(t.duration_minutes) : 0), 0);
const DAY_MINUTES = 8 * 60;

/** How full a cell is, for its tint: 0 empty, 1 light, 2 busy, 3 heavy. */
const loadOf = (items, { day = false } = {}) => {
  if (items.length === 0) return 0;
  const m = day ? minutesOf(items) : 0;
  if (m > 0) return m <= DAY_MINUTES / 2 ? 1 : m <= DAY_MINUTES ? 2 : 3;
  return items.length <= 2 ? 1 : items.length <= 4 ? 2 : 3;
};

const NOBODY = '_nobody';
const laneOf = (item) => item.plate_key || NOBODY;
const firstName = (name) => String(name || 'them').split(/\s+/)[0];

/** Whether `item` may go to lane `key`: its own lane, or a member of its (shared) list, or nobody there. */
const mayGo = (item, key, members) => {
  if (key === laneOf(item)) return true;
  if (!item.shared) return false;
  if (key === NOBODY) return true;
  return (members[item.list_id] || []).includes(key);
};

/** One to-do, compact. A button (opens it, when it is on a list of mine), a drag handle, a ⋯. */
function Card({ item, busy, onOpen, onDragStart, menu, dragging }) {
  const p = Number(item.priority) || 4;
  const blocked = Number(item.blocked) || 0;
  return (
    <li className={`twl-card is-p${p}${dragging ? ' is-dragging' : ''}${item.can_edit ? '' : ' is-readonly'}`}
        data-wl-item={item.id} onPointerDown={(e) => onDragStart(e, item)}>
      <span className={`twl-prio p${p}`} aria-hidden="true" />
      <span className="twl-card-main">
        {item.can_edit ? (
          <button type="button" className="twl-title" onClick={() => onOpen(item)}
                  title={`${item.title} · ${item.list_name}${item.parent_title ? ` › in ${item.parent_title}` : ''}`}>
            {item.title}
          </button>
        ) : (
          <span className="twl-title" title={`${item.title} · ${item.list_name} (you are not on this list)`}>{item.title}</span>
        )}
        <span className="twl-meta">
          {blocked > 0 && (
            <span className="twl-lock" title={`Waiting on ${blocked} more`} aria-label={`Waiting on ${blocked} more`}>
              <LockIcon size={11} />
            </span>
          )}
          {item.due_time && <span className="twl-time">{item.due_time}</span>}
          {Number(item.duration_minutes) > 0 && (
            <span className="twl-time twl-dur" data-wl-duration={item.duration_minutes}>{durationLabel(item.duration_minutes)}</span>
          )}
          {item.company && <ClientChip company={item.company} />}
          <span className="twl-list" title={item.list_name}>
            <span className={`tda-dot ${swatch(item.list_color)}`} aria-hidden="true" />
            <span className="twl-list-name">{item.list_name}</span>
          </span>
        </span>
      </span>
      <MoreMenu label={`More for "${item.title}"`} disabled={busy} items={menu(item)} />
    </li>
  );
}

/**
 * The workload. `api` is the page's (/todos); `me` the viewer's key;
 * `companies` the clients for the filter (null: none to offer); `lists` the
 * viewer's own lists, for nothing more than names; `onOpen(item)` opens a
 * to-do in its list; `onChanged` tells the page something moved.
 */
export default function TodoWorkload({ api, me, companies = null, narrow = false, onOpen, onChanged = null }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [listId, setListId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState(() => new Set());   // cells opened past SHOW
  const [give, setGive] = useState(null);              // the to-do in "Give to…"
  const [plan, setPlan] = useState(null);              // the to-do in "Reschedule…"
  const [drag, setDrag] = useState(null);              // { id, x, y, over: { lane, col, ok } }
  const dragRef = useRef(null);
  const swallowClick = useRef(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      const q = new URLSearchParams();
      if (listId) q.set('list_id', listId);
      if (companyId) q.set('company_id', companyId);
      const s = q.toString();
      const r = await api.get(`/workload${s ? `?${s}` : ''}`);
      setData(r.data && Array.isArray(r.data.items) ? r.data : { items: [], people: [], list_members: {}, lists: [] });
    } catch (err) {
      setData((d) => d || { items: [], people: [], list_members: {}, lists: [] });
      setError(err.response?.data?.error || 'Could not load the workload');
    }
  }, [api, listId, companyId]);
  useEffect(() => { load(); }, [load]);

  // The `as=` of the page's api is in the path's query; a filter adds to it.
  const today = data?.today || isoDay(new Date());
  const monday = data?.week_start || isoDay(addDays(new Date(), -(((new Date()).getDay() + 6) % 7)));
  const cols = useMemo(() => columnsFor(today, monday), [today, monday]);
  const members = useMemo(() => data?.list_members || {}, [data]);
  const people = useMemo(() => data?.people || [], [data]);
  const byKey = useMemo(() => new Map(people.map((p) => [p.key, p])), [people]);

  /** Lanes: each person, and "Unassigned" when there is anything on a shared list for nobody. */
  const lanes = useMemo(() => {
    const items = data?.items || [];
    const out = people.map((p) => ({ key: p.key, person: p, items: items.filter((t) => t.plate_key === p.key) }));
    const nobody = items.filter((t) => !t.plate_key);
    const anyShared = Object.keys(members).length > 0;
    if (nobody.length || anyShared) out.push({ key: NOBODY, person: null, items: nobody });
    return out;
  }, [data, people, members]);

  const cellItems = (lane, col) => lane.items.filter((t) => bucketOf(t, today, monday) === col.key);

  const say = (words) => { setNotice(words); setTimeout(() => setNotice((n) => (n === words ? null : n)), 5000); };

  /** Give `item` to lane `key`, and/or move it to column `col` - one action, then read again. */
  const act = async (item, { to, col }) => {
    setBusy(true);
    setError(null);
    try {
      const words = [];
      if (to !== undefined && to !== laneOf(item)) {
        await api.post('/workload/assign', { todo_id: item.id, to_key: to === NOBODY ? null : to });
        words.push(to === NOBODY ? 'Back to nobody' : `Given to ${firstName(byKey.get(to)?.name)}`);
      }
      if (col && col.target && bucketOf(item, today, monday) !== col.key) {
        const due = col.target === 'none' ? null : col.target;
        await api.patch(`/${item.id}`, { due_date: due });
        words.push(due ? `moved to ${longDay(due)}` : 'no date now');
      }
      if (words.length) {
        say(`“${item.title}”: ${words.join(', ')}`);
        await load();
        if (onChanged) onChanged();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'That did not go through');
      await load();
    } finally {
      setBusy(false);
    }
  };

  /* -------------------------------------------------------------- dragging */

  const cellAt = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const cell = el && el.closest ? el.closest('[data-wl-cell]') : null;
    if (!cell) return null;
    return { lane: cell.getAttribute('data-wl-lane'), col: cell.getAttribute('data-wl-col') };
  };
  const okTo = (item, over) => {
    if (!over) return false;
    const laneOk = mayGo(item, over.lane, members);
    const col = cols.find((c) => c.key === over.col);
    const sameCol = bucketOf(item, today, monday) === over.col;
    const colOk = sameCol || Boolean(item.can_edit && col && col.target);
    const moves = over.lane !== laneOf(item) || !sameCol;
    return laneOk && colOk && moves;
  };

  const startDrag = (e, item) => {
    if (narrow || busy || e.button !== 0 || e.pointerType === 'touch') return;
    if (e.target.closest('.tda-more')) return;
    dragRef.current = { item, x0: e.clientX, y0: e.clientY, on: false, pointerId: e.pointerId };
  };
  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      if (!d.on) {
        if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_AFTER) return;
        d.on = true;
        document.body.classList.add('twl-dragging');
      }
      e.preventDefault();
      const over = cellAt(e.clientX, e.clientY);
      d.over = over;
      setDrag({ id: d.item.id, title: d.item.title, x: e.clientX, y: e.clientY, over, ok: okTo(d.item, over) });
    };
    const up = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      dragRef.current = null;
      document.body.classList.remove('twl-dragging');
      setDrag(null);
      if (!d.on) return;
      swallowClick.current = Date.now();
      if (!okTo(d.item, d.over)) return;
      act(d.item, { to: d.over.lane, col: cols.find((c) => c.key === d.over.col) });
    };
    const cancel = () => { dragRef.current = null; document.body.classList.remove('twl-dragging'); setDrag(null); };
    const key = (e) => { if (e.key === 'Escape' && dragRef.current) cancel(); };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key);
      document.body.classList.remove('twl-dragging');
    };
    // act and okTo read the current answer; these say when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, cols, members, busy]);

  const open = (item) => { if (Date.now() - swallowClick.current < 400) return; onOpen(item); };

  /* ------------------------------------------------------------- the menus */

  const menu = (item) => [
    item.can_edit && { label: 'Open', icon: 'edit', onClick: () => onOpen(item) },
    item.shared && { label: 'Give to…', icon: 'person', onClick: () => setGive(item) },
    item.can_edit && { label: 'Reschedule…', icon: 'calendar', onClick: () => setPlan(item) },
    !item.shared && !item.can_edit && { label: 'Only its owner can change this', disabled: true, onClick: () => {} },
    !item.shared && item.can_edit && { label: 'Not on a shared list - nobody else to give it to', disabled: true, onClick: () => {} }
  ];

  const giveDialog = give && (() => {
    const keys = [...(members[give.list_id] || [])];
    const who = keys.map((k) => byKey.get(k) || { key: k, name: 'Somebody' })
      .sort((a, b) => (b.key === me) - (a.key === me) || String(a.name).localeCompare(String(b.name)));
    return (
      <Dialog label={`Give “${give.title}” to…`} onClose={() => setGive(null)} className="twl-dialog">
        <p className="tds-quiet">On {give.list_name}: anybody on the list.</p>
        <div className="twl-pick" role="group" aria-label="People on the list">
          {who.map((p) => (
            <button key={p.key} type="button" className="btn btn-secondary twl-pick-btn" data-give={p.key}
                    disabled={busy || p.key === give.assignee_key}
                    aria-current={p.key === give.assignee_key ? 'true' : undefined}
                    onClick={() => { setGive(null); act(give, { to: p.key }); }}>
              <Avatar name={p.name} who={p.key} size="xs" />
              <span>{p.name}{p.key === me ? ' (you)' : ''}</span>
              {p.is_contractor && <ItTag />}
              {p.key === give.assignee_key && <span className="tds-quiet">has it</span>}
            </button>
          ))}
          <button type="button" className="btn btn-secondary twl-pick-btn" data-give={NOBODY}
                  disabled={busy || !give.assignee_key}
                  onClick={() => { setGive(null); act(give, { to: NOBODY }); }}>
            <span>Nobody</span>
          </button>
        </div>
      </Dialog>
    );
  })();

  const planDialog = plan && (
    <Dialog label={`Reschedule “${plan.title}”`} onClose={() => setPlan(null)} className="twl-dialog">
      <div className="twl-pick" role="group" aria-label="When">
        {cols.filter((c) => c.target).map((c) => (
          <button key={c.key} type="button" className="btn btn-secondary twl-pick-btn" data-plan={c.key}
                  disabled={busy || bucketOf(plan, today, monday) === c.key}
                  onClick={() => { setPlan(null); act(plan, { col: c }); }}>
            <span>{c.label}</span>{c.sub && <span className="tds-quiet">{c.sub}</span>}
          </button>
        ))}
      </div>
    </Dialog>
  );

  /* ----------------------------------------------------------------- pieces */

  const laneName = (lane) => (lane.person ? lane.person.name : 'Unassigned');
  const laneHead = (lane) => {
    const overdue = lane.items.filter((t) => bucketOf(t, today, monday) === 'overdue').length;
    return (
      <div className="twl-who" data-wl-who={lane.key}>
        {lane.person ? <Avatar name={lane.person.name} who={lane.person.key} />
          : <span className="twl-nobody" aria-hidden="true"><Icon name="person" size={14} /></span>}
        <span className="twl-who-main">
          <span className="twl-who-name">
            {laneName(lane)}{lane.person?.is_contractor && <ItTag />}
          </span>
          {lane.person?.me && <span className="twl-who-you">you</span>}
          <span className="twl-who-n">
            {lane.items.length} open{minutesOf(lane.items) > 0 && (
              <span className="twl-who-hours" data-wl-hours={minutesOf(lane.items)}>{` · ${durationLabel(minutesOf(lane.items))}`}</span>
            )}{overdue ? <span className="twl-who-late"> · {overdue} overdue</span> : null}
          </span>
        </span>
      </div>
    );
  };

  const filters = (
    <div className="twl-filters" role="group" aria-label="Filter the workload">
      <label className="twl-filter">
        <span>List</span>
        <select aria-label="Filter by list" value={listId} onChange={(e) => setListId(e.target.value)}>
          <option value="">All lists</option>
          {(data?.lists || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </label>
      {companies && companies.length > 0 && (
        <label className="twl-filter">
          <span>Client</span>
          <select aria-label="Filter by client" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">All clients</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{c.archived ? ' (archived)' : ''}</option>)}
          </select>
        </label>
      )}
      <span className="twl-week">Week of {longDay(monday)}</span>
      <span className="twl-scope">{data?.scope === 'all' ? 'Everybody' : 'You and the people on your lists'}</span>
    </div>
  );

  if (data === null) return <div className="twl"><p className="ctd-quiet">Loading…</p></div>;

  const status = (
    <>
      {error && <p className="ctd-error" role="alert">{error}</p>}
      <p className="twl-notice" role="status" aria-live="polite">{notice || ''}</p>
    </>
  );

  /* -------------------------------------------------- on a phone: stacked */

  if (narrow) {
    return (
      <div className="twl is-narrow">
        {filters}
        {status}
        {lanes.map((lane) => (
          <section key={lane.key} className="twl-stack" data-wl-lane-stack={lane.key} aria-label={laneName(lane)}>
            <header className="twl-stack-head">{laneHead(lane)}</header>
            <ul className="twl-sums" aria-label={`${laneName(lane)} by when`}>
              {cols.filter((c) => c.key !== 'later' || cellItems(lane, c).length).map((c) => {
                const n = cellItems(lane, c).length;
                return (
                  <li key={c.key} className={`twl-sum${c.key === 'overdue' && n ? ' is-late' : ''}`}
                      data-load={loadOf(cellItems(lane, c), { day: Boolean(c.day) })}>
                    <span className="twl-sum-n">{n}</span><span className="twl-sum-label">{c.label}</span>
                    {minutesOf(cellItems(lane, c)) > 0 && (
                      <span className="twl-sum-hours">{durationLabel(minutesOf(cellItems(lane, c)))}</span>
                    )}
                  </li>
                );
              })}
            </ul>
            {cols.map((c) => {
              const items = cellItems(lane, c);
              if (!items.length) return null;
              return (
                <div key={c.key} className="twl-stack-group" data-wl-cell="" data-wl-lane={lane.key} data-wl-col={c.key}>
                  <h4 className={`twl-stack-label${c.key === 'overdue' ? ' is-late' : ''}`}>{c.label}{c.sub ? ` · ${c.sub}` : ''}</h4>
                  <ul className="twl-cards">
                    {items.map((t) => (
                      <Card key={t.id} item={t} busy={busy} onOpen={open} onDragStart={() => {}} menu={menu} />
                    ))}
                  </ul>
                </div>
              );
            })}
            {lane.items.length === 0 && <p className="twl-empty">Nothing open.</p>}
          </section>
        ))}
        {giveDialog}
        {planDialog}
      </div>
    );
  }

  /* ------------------------------------------------ on a desk: the grid */

  const dragItem = drag ? (data.items || []).find((t) => t.id === drag.id) : null;
  return (
    <div className={`twl${drag ? ' is-dragging' : ''}`}>
      {filters}
      {status}
      {(data.items || []).length === 0 ? (
        <div className="ctd-empty ctd-empty-state twl-none" role="status">
          <span className="ctd-empty-ic" aria-hidden="true"><Icon name="done" size={28} /></span>
          <p className="ctd-empty-title">Nothing open</p>
          <p className="ctd-empty-text">Nobody here has anything open{listId || companyId ? ' for that filter' : ''}.</p>
        </div>
      ) : null}
      <div className="twl-scroll">
        <table className="twl-grid" aria-label="Workload by person and by when">
          <thead>
            <tr>
              <th scope="col" className="twl-corner">Person</th>
              {cols.map((c) => (
                <th key={c.key} scope="col"
                    className={`twl-col is-${c.key === 'overdue' ? 'late' : c.day === today ? 'today' : 'day'}${c.day && [0, 6].includes(new Date(`${c.day}T12:00:00`).getDay()) ? ' is-weekend' : ''}`}
                    data-wl-head={c.key}>
                  <span className="twl-col-label">{c.label}</span>
                  {c.sub && <span className="twl-col-sub">{c.sub}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lanes.map((lane) => (
              <tr key={lane.key} data-wl-lane-row={lane.key}
                  className={dragItem ? (mayGo(dragItem, lane.key, members) ? 'is-open-lane' : 'is-shut-lane') : undefined}>
                <th scope="row" className="twl-lane-head">{laneHead(lane)}</th>
                {cols.map((c) => {
                  const items = cellItems(lane, c);
                  const id = `${lane.key}|${c.key}`;
                  const showAll = more.has(id);
                  const over = drag && drag.over && drag.over.lane === lane.key && drag.over.col === c.key;
                  return (
                    <td key={c.key} data-wl-cell="" data-wl-lane={lane.key} data-wl-col={c.key}
                        data-load={loadOf(items, { day: Boolean(c.day) })}
                        data-wl-minutes={minutesOf(items) || undefined}
                        className={`twl-cell${c.key === 'overdue' && items.length ? ' is-late' : ''}${c.day === today ? ' is-today' : ''}${over ? (drag.ok ? ' is-drop' : ' is-no-drop') : ''}`}
                        aria-label={`${laneName(lane)}, ${c.label}: ${items.length}${minutesOf(items) ? `, ${durationLabel(minutesOf(items))}` : ''}`}>
                      {items.length > 0 && (
                        <span className="twl-n" aria-hidden="true">
                          {items.length}{minutesOf(items) > 0 && <span className="twl-n-hours">{` · ${durationLabel(minutesOf(items))}`}</span>}
                        </span>
                      )}
                      <ul className="twl-cards">
                        {(showAll ? items : items.slice(0, SHOW)).map((t) => (
                          <Card key={t.id} item={t} busy={busy} onOpen={open} onDragStart={startDrag}
                                menu={menu} dragging={drag && drag.id === t.id} />
                        ))}
                      </ul>
                      {items.length > SHOW && (
                        <button type="button" className="tda-add-line twl-more"
                                onClick={() => setMore((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(id)) next.delete(id); else next.add(id);
                                  return next;
                                })}>
                          {showAll ? 'Fewer' : `+${items.length - SHOW} more`}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.truncated && <p className="tds-quiet">Showing the first {data.items.length} - filter by a list or a client to see the rest.</p>}
      <p className="twl-help">Drag a to-do to another person to give it to them, or to another day to move it. Each card’s ⋯ does the same from the keyboard.</p>
      {drag && (
        <div className={`twl-ghost${drag.ok ? ' is-ok' : ''}`} aria-hidden="true" style={{ left: drag.x + 12, top: drag.y + 8 }}>
          {drag.title}
        </div>
      )}
      {giveDialog}
      {planDialog}
    </div>
  );
}
